import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Shield, ShieldAlert, ShieldCheck, X, Lock, Clock, AlertTriangle } from 'lucide-react'
import { cn } from '@/utils'
import type { PendingAuthorization, AuthorizationTier } from '@/hooks/useAuthorization'

interface AuthorizationModalProps {
  pendingAuth: PendingAuthorization | null
  timeRemaining: number
  onApprove: (pin?: string) => void
  onDeny: () => void
}

/**
 * Get tier-specific styling and icons
 */
function getTierConfig(tier: AuthorizationTier) {
  switch (tier) {
    case 'CRITICAL':
      return {
        icon: ShieldAlert,
        iconBg: 'bg-danger/20',
        iconColor: 'text-danger',
        headerBg: 'bg-gradient-to-r from-danger to-danger/80',
        label: 'Critical Action',
        description: 'This action requires PIN verification',
        progressColor: 'bg-danger',
      }
    case 'ELEVATED':
      return {
        icon: Shield,
        iconBg: 'bg-warning/20',
        iconColor: 'text-warning',
        headerBg: 'bg-gradient-to-r from-warning to-warning/80',
        label: 'Elevated Permission',
        description: 'Optional PIN for extra security',
        progressColor: 'bg-warning',
      }
    case 'BUTTON_PRESS':
    default:
      return {
        icon: ShieldCheck,
        iconBg: 'bg-primary/20',
        iconColor: 'text-primary',
        headerBg: 'bg-gradient-to-r from-primary to-primary/80',
        label: 'Authorization Required',
        description: 'Niki needs your permission to proceed',
        progressColor: 'bg-primary',
      }
  }
}

export function AuthorizationModal({
  pendingAuth,
  timeRemaining,
  onApprove,
  onDeny,
}: AuthorizationModalProps) {
  const [pin, setPin] = useState('')
  const [showPin, setShowPin] = useState(false)
  const [pinError, setPinError] = useState<string | null>(null)

  // Reset state when modal opens/closes
  useEffect(() => {
    if (pendingAuth) {
      setPin('')
      setShowPin(false)
      setPinError(null)
    }
  }, [pendingAuth])

  const handleApprove = useCallback(() => {
    if (pendingAuth?.tier === 'CRITICAL' && pin.length < 4) {
      setPinError('Please enter your PIN to proceed')
      return
    }
    onApprove(pin || undefined)
  }, [pendingAuth, pin, onApprove])

  const handlePinChange = (value: string) => {
    // Only allow digits
    const digits = value.replace(/\D/g, '').slice(0, 6)
    setPin(digits)
    setPinError(null)
  }

  if (!pendingAuth) return null

  const tierConfig = getTierConfig(pendingAuth.tier)
  const TierIcon = tierConfig.icon
  const progressPercent = (timeRemaining / 30) * 100

  return (
    <AnimatePresence>
      {pendingAuth && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            onClick={onDeny}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-light-card dark:bg-dark-card rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className={cn('p-5 relative', tierConfig.headerBg)}>
                {/* Progress bar for timeout */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                  <motion.div
                    className={cn('h-full', tierConfig.progressColor)}
                    initial={{ width: '100%' }}
                    animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 1, ease: 'linear' }}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ rotate: -15, scale: 0.8 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className={cn('w-14 h-14 rounded-xl flex items-center justify-center', tierConfig.iconBg)}
                  >
                    <TierIcon className={cn('w-8 h-8', tierConfig.iconColor)} />
                  </motion.div>
                  <div className="flex-1">
                    <h2 className="font-heading text-xl text-white font-semibold">
                      {tierConfig.label}
                    </h2>
                    <p className="text-sm text-white/80">{tierConfig.description}</p>
                  </div>
                  <button
                    onClick={onDeny}
                    className="p-2 text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 space-y-5">
                {/* Tool Info */}
                <div className="glass-card p-4 rounded-xl">
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1">
                    Niki wants to:
                  </p>
                  <p className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                    {pendingAuth.displayName}
                  </p>
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
                    {pendingAuth.details}
                  </p>
                </div>

                {/* PIN Input for ELEVATED and CRITICAL */}
                {(pendingAuth.tier === 'ELEVATED' || pendingAuth.tier === 'CRITICAL') && (
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                      <Lock className="w-4 h-4" />
                      {pendingAuth.tier === 'CRITICAL' ? 'Enter PIN to Authorize' : 'Optional: Enter PIN'}
                    </label>
                    <div className="relative">
                      <input
                        type={showPin ? 'text' : 'password'}
                        value={pin}
                        onChange={(e) => handlePinChange(e.target.value)}
                        placeholder={pendingAuth.tier === 'CRITICAL' ? 'Required' : 'Optional'}
                        className={cn(
                          'w-full px-4 py-3 rounded-xl text-lg tracking-widest text-center',
                          'bg-light-surface dark:bg-dark-surface',
                          'border-2 transition-colors',
                          pinError
                            ? 'border-danger focus:border-danger'
                            : 'border-transparent focus:border-primary',
                          'text-light-text-primary dark:text-dark-text-primary',
                          'placeholder:text-light-text-tertiary dark:placeholder:text-dark-text-tertiary',
                          'focus:outline-none'
                        )}
                        maxLength={6}
                        inputMode="numeric"
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPin(!showPin)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-light-text-tertiary hover:text-light-text-secondary dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary transition-colors"
                      >
                        {showPin ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {pinError && (
                      <motion.p
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-danger flex items-center gap-1"
                      >
                        <AlertTriangle className="w-4 h-4" />
                        {pinError}
                      </motion.p>
                    )}
                  </div>
                )}

                {/* Countdown warning */}
                <div className="flex items-center justify-center gap-2 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
                  <Clock className="w-4 h-4" />
                  <span>
                    Auto-denying in{' '}
                    <span
                      className={cn(
                        'font-mono font-semibold',
                        timeRemaining <= 10 && 'text-danger'
                      )}
                    >
                      {timeRemaining}s
                    </span>
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="p-5 pt-0 flex gap-3">
                <motion.button
                  onClick={onDeny}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-secondary flex-1 py-3"
                >
                  Deny
                </motion.button>
                <motion.button
                  onClick={handleApprove}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={pendingAuth.tier === 'CRITICAL' && pin.length < 4}
                  className={cn(
                    'btn-primary flex-1 py-3 flex items-center justify-center gap-2',
                    pendingAuth.tier === 'CRITICAL' && pin.length < 4 && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <ShieldCheck className="w-5 h-5" />
                  Allow
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
